import { getOptjobAsObject, OptJobStatus, OptJobType, useLatestOptJobStatus } from 'api/optJobs/optJobs.api'
import { getOptimizationFailureExplanationKey, optJobIsDone } from 'helpers/optimize.helper/optimize.helper'
import i18n from 'i18next'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'

type OptimizeButtonProps = {
  creatingOptJob?: boolean
  disabled?: boolean
  isValid?: boolean
  onClick?: () => void
  sandboxProjectId?: number
  style?: React.CSSProperties
  type: OptJobType
  optimizeLabel?: string
  subtypes?: string[]
}

export function buttonTranslationMapping(status: OptJobStatus | undefined, optimizeLabel?: string): string | undefined {
  if (status === null || status === undefined || status === 'Finished') {
    return optimizeLabel ?? i18n.t('Optimize production plan')
  }
  const map: { [key: string]: string } = {
    Created: i18n.t('Fetching optimization data'),
    Queued: i18n.t('Preparing optimization'),
    Running: i18n.t('Optimizing'),
    Failed: i18n.t('Failed'),
    Unsolvable: i18n.t('Unsolvable'),
  }
  return map[status]
}

// FIXME: Move this to molecule
export function OptimizeButton({
  creatingOptJob,
  disabled,
  isValid,
  onClick,
  sandboxProjectId,
  style,
  type,
  optimizeLabel,
  subtypes,
}: OptimizeButtonProps): ReactElement {
  const { t } = useTranslation()
  const { data: latestOptJob, status: fetchingLatestJobStatus } = useLatestOptJobStatus({
    sandboxProjectId,
    type,
    subtypes,
  })
  const firstLatestOptJob = getOptjobAsObject(latestOptJob)

  const labelOverride = useMemo(
    () => (fetchingLatestJobStatus === 'pending' ? t('Fetching optimization status') : undefined),
    [fetchingLatestJobStatus, t]
  )

  const failureExplanationKey = getOptimizationFailureExplanationKey(firstLatestOptJob?.status)
  const isLoading = creatingOptJob || fetchingLatestJobStatus === 'pending' || !optJobIsDone(firstLatestOptJob)
  const label = labelOverride ?? buttonTranslationMapping(firstLatestOptJob?.status, optimizeLabel && t(optimizeLabel))

  return (
    <NewButton
      variant="optimize"
      label={label ?? ''}
      icon="fal fa-redo-alt"
      loading={isLoading}
      // `loading` alone does not block a click here the way ModuleHeaderButton's did, and the two
      // states that appear only in it — a job being created, its status being fetched — still have
      // to stop a second one.
      disabled={Boolean(isLoading || !isValid || disabled)}
      maxWidth="400px"
      tooltip={failureExplanationKey ? { title: t(failureExplanationKey) } : undefined}
      style={style}
      data-testid="optimize-button" // e2e: start-optimization button, decoupled from display copy — do not remove
      onClick={() => onClick?.()}
    />
  )
}
